import { Field } from '../../components/table'
import React, { useEffect, useState } from 'react'
import { Actions, Pager } from '../../components/table/types'
import viewIcon from '../../assets/table_icons/view.svg'
import editIcon from '../../assets/table_icons/edit.svg'
import deleteIcon from '../../assets/table_icons/delete.svg'
import { useTranslation } from 'react-i18next'
import { getContentContainer } from '../../container/treatment-module'
import { ITreatmentService, TREATMENT_SERVICE_KEY } from '../../modules/treatments'
import { LoggedUserService } from '../../modules/users/services/LoggedUserService'
import { IUserService, LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from '../../modules/users'
import { getUserContainer } from '../../container/user-module'
import { Treatment } from '../../modules/treatments/models/Treatment'
import { Query } from '../../common/api/Query'
import { CustomModal } from '../../components/modal/CustomModal'
import { Box, Button, Modal } from '@mui/material'
import { Pagination } from '../../components/table/Pagination'
import styles from './MedicineTable.module.css'
import newEvent from '../../assets/calendar/add.svg'
import { MedicineModal, MedicineModalMode } from './MedicineModal'
import { AppTable } from '../../components/table'
import { UnitType, unitTypeTypes } from '../../modules/treatments/enums/UnitType'
import { PageLayout } from 'pages/layout'

type TreatmentTableProps = {
  id?: string
}

const userContainer = getUserContainer()
const treatmentService = getContentContainer().get<ITreatmentService>(TREATMENT_SERVICE_KEY)
const loggedUserService = userContainer.get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)

export function MedicineTable(props: TreatmentTableProps) {
  const { t } = useTranslation()
  const loggedUser = loggedUserService.get()
  const [treatments, setTreatments] = useState<Treatment[]>([])
  const [treatmentsPerPage, setTreatmentsPerPage] = useState<number>(10)
  const [page, setPage] = useState<number>(0)
  const [creators, setCreators] = useState<Map<string, string>>(new Map())
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [count, setCount] = useState<number>(0)
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false)
  const [currentTreatment, setCurrentTreatment] = useState<Treatment>()
  const [openMedicineModal, setOpenMedicineModal] = useState<boolean>(false)
  const [modalMode, setModalMode] = useState<MedicineModalMode>(MedicineModalMode.CREATE)
  const innerWidth = window.innerWidth

  const getUnitLabel = (unit: number): string => {
    const unitTypes = unitTypeTypes()
    return unitTypes[unit as UnitType] || ''
  }

  useEffect(() => {
    if (!isLoading) {
      return
    }
    treatmentService
      .getFilteredList(
        new Query({
          query: [],
          pager: { limit: treatmentsPerPage, offset: page * treatmentsPerPage },
        })
      )
      .subscribe((res: any) => {
        setCount(res.count || 0)
        setTreatments(res.items || [])
        setIsLoading(false)
      })
  }, [isLoading, page, treatmentsPerPage])

  useEffect(() => {
    if (!treatments.length) {
      return
    }
    const ids = treatments.map((t) => t.creatorID)
    userService
      .getFilteredList(
        new Query({
          pager: { offset: 0, limit: ids.length },
          query: [{ name: 'ids', value: ids }],
        })
      )
      .subscribe((res) =>
        setCreators(new Map(res.items.map((u) => [u.id, `${u.firstName} ${u.lastName}`])))
      )
  }, [treatments])

  const handlePaginationChange = (event: unknown, value: number) => {
    setPage(value)
    setIsLoading(true)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (Number.isNaN(event.target.value)) {
      setTreatmentsPerPage(10)
      return
    }
    setTreatmentsPerPage(Number.parseInt(event.target.value))
    setPage(0)
    setIsLoading(true)
  }

  const pager: Pager | undefined =
    count > 0
      ? {
          page: page,
          count: count,
          handleChangePage: handlePaginationChange,
          rowsPerPage: treatmentsPerPage,
          handleChangeRowsPerPage,
        }
      : undefined

  const seeTreatment = (t: Treatment) => {
    setCurrentTreatment(t)
    setModalMode(MedicineModalMode.VIEW)
    setOpenMedicineModal(true)
  }

  const editTreatment = (t: Treatment) => {
    setCurrentTreatment(t)
    setModalMode(MedicineModalMode.EDIT)
    setOpenMedicineModal(true)
  }

  const removeTreatment = (t: Treatment) => {
    setCurrentTreatment(t)
    setOpenDeleteModal(true)
  }

  const handleCloseDeleteModal = () => setOpenDeleteModal(false)

  const handleDeleteTreatment = () => {
    if (currentTreatment?.id) {
      treatmentService.delete(currentTreatment.id).subscribe(() => {
        setIsLoading(true)
        setOpenDeleteModal(false)
      })
    }
  }

  const handleNewTreatment = () => {
    setCurrentTreatment(undefined)
    setModalMode(MedicineModalMode.CREATE)
    setOpenMedicineModal(true)
  }

  const handleCloseMedicineModal = () => {
    setOpenMedicineModal(false)
  }

  const handleLoading = () => {
    setIsLoading(true)
  }

  const isCreator = (t: Treatment): boolean => {
    return t.creatorID === loggedUser?.id
  }

  const fields: Field<Treatment>[] = [
    {
      name: 'name',
      label: t('name'),
    },
    {
      name: 'dose',
      label: t('dosePerIntake'),
      renderFunc: (field, item) => {
        return `${item.dose} ${getUnitLabel(item.unit)}`
      },
    },
    {
      name: 'creatorID',
      label: t('creator'),
      renderFunc: (field, item) => {
        return creators.get(item.creatorID) || ''
      },
    },
  ]

  const actions: Actions<Treatment> = {
    actionsColumn: t('actions'),
    items: [
      {
        handler: seeTreatment,
        icon: viewIcon,
        label: t('see'),
      },
      {
        handler: editTreatment,
        icon: editIcon,
        label: t('edit'),
        hidden: (t) => !isCreator(t),
      },
      {
        handler: removeTreatment,
        icon: deleteIcon,
        label: t('delete'),
        hidden: (t) => !isCreator(t),
      },
    ],
  }

  return (
    <PageLayout>
      <Modal open={openDeleteModal} onClose={handleCloseDeleteModal}>
        <CustomModal
          title={t('deleteMedicine')}
          warningText={t('irreversibleMedicineAction')}
          handleClose={handleCloseDeleteModal}
          handleSave={handleDeleteTreatment}
        />
      </Modal>

      {openMedicineModal && (
        <Modal open={openMedicineModal} onClose={handleCloseMedicineModal}>
          <MedicineModal
            mode={modalMode}
            handleClose={handleCloseMedicineModal}
            currentTreatment={currentTreatment}
            handleLoading={handleLoading}
          />
        </Modal>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4,
          height: '100%',
          width: '100%',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            order: { xs: 2, md: 1 },
          }}
        >
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <AppTable
              fields={fields}
              items={treatments}
              rowKeyField={'id'}
              actions={actions}
              isLoading={isLoading}
            />
          </Box>
          {pager && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
              <Pagination {...pager} />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            width: { xs: '100%', md: '280px' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
            order: { xs: 1, md: 2 },
            mb: { xs: 2, md: 0 },
          }}
        >
          <span className={styles.registeredMedicine}>Medicamentos registrados</span>
          <span className={styles.countMedicine}>{count > 0 ? count : '0'}</span>
          <Button
            className={styles.newTreatmentButton}
            type={'button'}
            onClick={handleNewTreatment}
          >
            <Box width={'100%'} display={'flex'} justifyContent={'space-around'}>
              <img src={newEvent} alt="newEvent" width={40} />
              <span className={styles.newTreatmentButtonText}>{t('newMedicine')}</span>
            </Box>
          </Button>
        </Box>
      </Box>
    </PageLayout>
  )
}
